import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminFunctions from "./AdminFunctions";
import AuthFunctions from "../services/AuthService";
import Session from "../auth/Session";
import {
    Container,
    Row,
    Col,
    Table,
    Button,
    Input,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Badge,
    Offcanvas,
    OffcanvasHeader,
    OffcanvasBody,
    Pagination,
    PaginationItem,
    PaginationLink,
} from "reactstrap";
import { BsFilter, BsChevronDown } from "react-icons/bs";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import Select from "react-select";
import AsyncSelect from "react-select/async";

const QB_CUSTOMER_MIN_SEARCH = 3;

// Returns a debounced version of an async function that resolves to its result.
const debounceAsync = (fn, delay) => {
    let timer;
    return (...args) =>
        new Promise((resolve, reject) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                fn(...args).then(resolve, reject);
            }, delay);
        });
};

const fetchQuickBooksCustomerOptions = async (inputValue) => {
    const term = (inputValue || "").trim();
    if (term.length < QB_CUSTOMER_MIN_SEARCH) return [];

    const token = Session.getToken();
    const response = await fetch(
        `api/JobListing/GetQuickBooksCustomers?search=${encodeURIComponent(term)}`,
        { headers: !token ? {} : { Authorization: `Bearer ${token}` } }
    );

    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to load QuickBooks customers.");
    }

    const data = await response.json();
    return (data || []).map((c) => ({ value: c.listId, label: c.displayName }));
};

const emptyJob = {
    id: 0,
    customerName: "",
    jobName: "",
    approvedOn: null,
    soldDate: null,
    manHours: null,
    rep: "",
    jobClass: "",
    address: "",
    equipmentIds: [],
    equipmentNames: "",
    materialRequirements: "",
    schedulingNotes: "",
    isOpen: true,
    invoiceNumber: null,
    quickBooksJobListId: null,
    selectedQuickBooksCustomerListId: null,
};

const canNavigate = ["Admin", "Office Admin", "Project Manager"];

const repOptions = [
    { value: "CG", label: "CG" },
    { value: "JWG", label: "JWG" },
    { value: "JPH", label: "JPH" },
    { value: "RWG", label: "RWG" },
    { value: "KPN", label: "KPN" },
    { value: "PJR", label: "PJR" }
];

const jobOptions = [
    { value: "Const", label: "Const" },
    { value: "Maint", label: "Maint" },
];

const JobListings = () => {
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [jobs, setJobs] = useState([]);
    const [jobSearch, setJobSearch] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortField, setSortField] = useState("soldDate");
    const [sortDir, setSortDir] = useState("asc");
    const [page, setPage] = useState(1);
    const [pageSize] = useState(15);
    const [totalCount, setTotalCount] = useState(0);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filters, setFilters] = useState({
        jobClasses: [],
        missing: {
            manHours: false,
            equipment: false,
            materialRequirements: false,
            schedulingNotes: false,
            approved: false,
        },
        showHiddenJobs: false,
    });

    const [equipment, setEquipment] = useState([]);
    const [canApprove, setCanApprove] = useState(false);

    // Single merged modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("edit"); // "edit" | "add"

    const [form, setForm] = useState(emptyJob);
    const [toggleMissingFilter, setToggleMissingFilter] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        async function isAuth() {
            const isAuthenticated = await AuthFunctions.getAuthenticatedAsync();
            if (!isAuthenticated) navigate("/login");
        }

        const role = Session.getRole();

        if (role === "" || !canNavigate.includes(role)) {
            navigate("/accessdenied");
            return;
        }

        async function fetchCanApprove() {
            const role = Session.getRole();
            if (role === "Project Manager") {
                setCanApprove(true);
                return;
            }
            setCanApprove(false);
        }

        isAuth();
        fetchCanApprove();
    }, [navigate]);

    useEffect(() => {
        const loadEquipment = async () => {
            try {
                const equipmentData = await getEquipment();

                const filteredEquipment = (equipmentData || []).filter(
                (e) =>
                    e.isActive !== false &&
                    e.category !== "Vehicle" &&
                    e.category !== "Trailer"
                );

                setEquipment(filteredEquipment);
            } catch (e) {
                setError(e?.message || "Failed to load equipment.");
            }
        };

        loadEquipment();
    }, []);

    const toggleModal = () => setIsModalOpen((v) => !v);

    const handleSort = (field) => {
        if (sortField !== field) {
            setSortField(field);
            setSortDir("asc");
        } else if (sortDir === "asc") {
            setSortDir("desc");
        } else {
            // Third click clears the column back to the default sort.
            setSortField("soldDate");
            setSortDir("asc");
        }
        setPage(1);
    };

    const renderSortIcon = (field) => {
        if (sortField !== field) return <FaSort />;
        return sortDir === "asc" ? <FaSortUp /> : <FaSortDown />;
    };

    const handleSearchSubmit = () => {
        setPage(1);
        setSearchTerm(jobSearch.trim());
    };

    const openEditModal = (row) => {
        setModalMode("edit");
        setForm({ ...emptyJob, ...row });
        setIsModalOpen(true);
    };

    const openAddModal = () => {
        setModalMode("add");
        setForm({ ...emptyJob });
        setIsModalOpen(true);
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError("");

            const payload = {
                id: form.id,
                isOpen: !!form.isOpen,
                materialRequirements: form.materialRequirements?.trim() || "",
                schedulingNotes: form.schedulingNotes?.trim() || "",
                equipmentIds: form.equipmentIds || [],
                manHours:
                    form.manHours === "" ||
                    form.manHours === null ||
                    form.manHours === undefined
                    ? 0
                    : Number(form.manHours),
                address: form.address,
            };

            payload.customerName = form.customerName?.trim() || "";
            payload.jobName = form.jobName?.trim() || "";
            payload.soldDate = form.soldDate || null;
            payload.rep = form.rep || "";
            payload.jobClass = form.jobClass || "";
            payload.quickBooksJobListId = form.quickBooksJobListId?.trim() || form.invoiceNumber?.trim() || null;
            payload.selectedQuickBooksCustomerListId = form.selectedQuickBooksCustomerListId || null;

            if (modalMode === "add" && !payload.selectedQuickBooksCustomerListId) {
                throw new Error("QuickBooks customer selection is required for manual job creation.");
            }

            if (modalMode === "add") {
                await createJob(payload);
            } else {
                await updateJob(form.id, payload);
            }

            await fetchJobs();
            setIsModalOpen(false);
        } catch (e) {
            setError(e?.message || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async (job) => {
        try {
            setSaving(true);
            setError("");

            const payload = {
                id: job.id,
                approvedOn: new Date().toISOString(),
            };

            await updateJob(job.id, payload);

            await fetchJobs();
        } catch (e) {
            setError(e?.message || "Approve failed.");
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;
        return d.toLocaleDateString();
    };

    const jobTitle = (row) => {
        const a = row.customerName || "";
        const b = row.jobName || "";
        const title = [a, b].filter(Boolean).join(" - ");
        return title || "-";
    };

    const truncateStyle = {
        maxWidth: 260,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

    const pageWindow = useMemo(() => {
        const windowSize = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + windowSize - 1);
        start = Math.max(1, end - windowSize + 1);

        const pages = [];
        for (let p = start; p <= end; p++) pages.push(p);
        return pages;
    }, [page, totalPages]);

    const equipmentOptions = useMemo(() => {
        return (equipment || []).map((e) => ({
            value: e.id,
            label: e.equipmentName,
        }));
    }, [equipment]);

    const selectedEquipmentOptions = useMemo(() => {
        const ids = form.equipmentIds || [];
        return equipmentOptions.filter((opt) => ids.includes(opt.value));
    }, [form.equipmentIds, equipmentOptions]);

    const crewDaysLabel = useMemo(() => {
        const mh = Number(form.manHours);
        if (!mh || mh <= 0) return "-";
        const crewDays = Math.ceil(mh / 20);
        return `${crewDays} Crew Day${crewDays === 1 ? "" : "s"}`;
    }, [form.manHours]);

    const loadQuickBooksCustomerOptions = useMemo(
        () => debounceAsync(fetchQuickBooksCustomerOptions, 350),
        []
    );

    const selectedQuickBooksCustomerOption = useMemo(() => {
        if (!form.selectedQuickBooksCustomerListId) return null;
        return {
            value: form.selectedQuickBooksCustomerListId,
            label: form.customerName || form.selectedQuickBooksCustomerListId,
        };
    }, [form.selectedQuickBooksCustomerListId, form.customerName]);

    // Derived: does this row already have a QuickBooks invoice number?
    const hasInvoiceNumber = !!(form.quickBooksJobListId);

    // ---------------- API CALLS ----------------
    const fetchJobs = useCallback(async () => {
        const token = Session.getToken();
        try {
            setError("");
            setLoading(true);

            const query = {
                search: searchTerm,
                sortField,
                sortDir,
                page,
                pageSize,
                jobClasses: filters.jobClasses,
                missingManHours: filters.missing.manHours,
                missingEquipment: filters.missing.equipment,
                missingMaterialRequirements: filters.missing.materialRequirements,
                missingSchedulingNotes: filters.missing.schedulingNotes,
                missingApproved: filters.missing.approved,
                showHiddenJobs: filters.showHiddenJobs,
            };

            const response = await fetch(`api/JobListing/Search`, {
                method: "POST",
                headers: !token
                    ? { "Content-Type": "application/json" }
                    : { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(query),
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || "Failed to load job listings.");
            }

            const data = await response.json();
            setJobs(data.items || []);
            setTotalCount(data.totalCount || 0);
        } catch (e) {
            setError(e?.message || "Failed to load job listings.");
        } finally {
            setLoading(false);
        }
    }, [searchTerm, sortField, sortDir, page, pageSize, filters]);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    // Reset to the first page whenever the active filters change (not on mount).
    const filtersInitialized = useRef(false);
    useEffect(() => {
        if (!filtersInitialized.current) {
            filtersInitialized.current = true;
            return;
        }
        setPage(1);
    }, [filters]);

    const updateJob = async (id, payload) => {
        const token = Session.getToken();
        const response = await fetch(`api/JobListing/${id}`, {
        method: "PUT",
        headers: !token
            ? { "Content-Type": "application/json" }
            : {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || "Failed to update job.");
        }
    };

    const createJob = async (payload) => {
        const token = Session.getToken();
        const response = await fetch(`api/JobListing/Create`, {
            method: "POST",
            headers: !token
                ? { "Content-Type": "application/json" }
                : { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || "Failed to create job.");
        }

        return await response.json(); // { id: newId }
    };

    const removeJob = async (id) => {
        const token = Session.getToken();
        const response = await fetch(`api/JobListing/Remove/${id}`, {
            method: "DELETE",
            headers: !token
                ? { "Content-Type": "application/json" }
                : { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || "Failed to remove job.");
        }

        await fetchJobs();
        setIsModalOpen(false);
    };

    const getEquipment = async () => {
        const token = Session.getToken();
        const response = await fetch(`api/Equipment/GetAll`, {
            headers: !token ? {} : { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || "Failed to load equipment.");
        }

        return await response.json();
    };

    const activeFilterCount = useMemo(() => {
        let n = 0;
        if (filters.jobClasses.length) n++;
        if (filters.missing.equipment) n++;
        if (filters.missing.manHours) n++;
        if (filters.missing.schedulingNotes) n++;
        if (filters.missing.materialRequirements) n++;
        if (filters.missing.approved) n++;
        if (filters.showHiddenJobs) n++;
        return n;
    }, [filters]);

    // ---------------- UI ----------------
    return (
        <div>
            {error ? (
                <Row>
                    <Col>
                        <div className="alert alert-danger mb-0">{error}</div>
                    </Col>
                </Row>
            ) : null}

            <Container className="container-main">
                {/* Header */}
                <Row className="align-items-center g-3">
                    <Col>
                        <h3>Job Listings</h3>
                    </Col>

                    <Col xs="12" md="auto">
                        <div className="d-flex gap-3 w-100">
                        <Button
                            color="primary"
                            className="flex-fill flex-md-grow-0 text-nowrap"
                            onClick={openAddModal}
                        >
                            + Add
                        </Button>
                        </div>
                    </Col>
                </Row>

                {loading ? (
                    <div
                        style={{
                        marginTop: "2rem",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        }}
                    >
                        <Spinner />
                    </div>
                ) : (
                <>
                    {/* Search + count */}
                    <Row className="row-20" style={{ marginTop: 10 }}>
                        <Col md="6">
                            <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "1.5rem",
                            }}
                            >
                                <Input
                                    type="text"
                                    value={jobSearch}
                                    onChange={(e) => setJobSearch(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleSearchSubmit();
                                        }
                                    }}
                                    placeholder="Search jobs..."
                                />
                                <Button
                                    color="primary"
                                    className="text-nowrap"
                                    onClick={handleSearchSubmit}
                                >
                                    Search
                                </Button>
                                <Button
                                    color="link"
                                    style={{ padding: 0 }}
                                    onClick={() => setFiltersOpen(true)}
                                >
                                    <div style={{ position: "relative" }}>
                                    <BsFilter size={24} />
                                    {activeFilterCount > 0 && (
                                        <Badge
                                        color="primary"
                                        pill
                                        style={{ position: "absolute", top: -10 }}
                                        >
                                        {activeFilterCount}
                                        </Badge>
                                    )}
                                    </div>
                                </Button>
                            </div>
                        </Col>

                        <Col
                            md="6"
                            className="d-flex justify-content-md-end align-items-center"
                        >
                            {/* <div
                            className="text-muted"
                            style={{ fontSize: 14, alignSelf: "end" }}
                            >
                                Showing {jobs.length} of {totalCount}
                            </div> */}
                        </Col>
                    </Row>

                    {/* Jobs table */}
                    <Row className="row-20">
                        <Col>
                            <Table bordered hover responsive>
                                <thead>
                                    <tr>
                                    {/* <th>Open</th> */}
                                    {/* <th>QuickBooks Job List ID</th> */}
                                    <th className="text-nowrap" style={{cursor: "pointer"}} onClick={() => handleSort("customerjob")}>Customer Name - Job Name {renderSortIcon("customerjob")}</th>
                                    <th>Approved</th>
                                    <th className="text-nowrap" style={{cursor: "pointer"}} onClick={() => handleSort("solddate")}><span>Sold Date {renderSortIcon("solddate")}</span></th>
                                    <th className="text-nowrap" style={{cursor: "pointer"}} onClick={() => handleSort("manhours")}>Man Hours {renderSortIcon("manhours")}</th>
                                    <th className="text-nowrap" style={{cursor: "pointer"}} onClick={() => handleSort("scheduledhours")}>Scheduled Hours {renderSortIcon("scheduledhours")}</th>
                                    <th className="text-nowrap" style={{cursor: "pointer"}} onClick={() => handleSort("loggedhours")}>Logged Hours {renderSortIcon("loggedhours")}</th>
                                    <th className="text-nowrap" style={{cursor: "pointer"}} onClick={() => handleSort("rep")}>Rep {renderSortIcon("rep")}</th>
                                    <th className="text-nowrap" style={{cursor: "pointer"}} onClick={() => handleSort("jobclass")}>Job Class {renderSortIcon("jobclass")}</th>
                                    <th>Address</th>
                                    <th>Equipment</th>
                                    <th>Material Requirements</th>
                                    <th>Scheduling Notes</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {jobs.map((row) => {
                                        const isApproved =
                                            !!row.approvedOn || row.approved === true;

                                        return (
                                            <tr
                                            key={row.id}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => openEditModal(row)}
                                            >
                                                {/* <td>
                                                    <Input
                                                    type="checkbox"
                                                    disabled
                                                    checked={row.isOpen}
                                                    name="active"
                                                    />
                                                </td>
                                                <td>{row.quickBooksJobListId ?? "-"}</td> */}
                                                <td 
                                                // style={{ ...truncateStyle, maxWidth: 340 }}
                                                >
                                                    {jobTitle(row)}
                                                </td>

                                                <td>
                                                    {isApproved ? (
                                                    formatDate(row.approvedOn)
                                                    ) : canApprove ? (
                                                    <Button
                                                        color="primary"
                                                        size="sm"
                                                        disabled={saving}
                                                        onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleApprove(row);
                                                        }}
                                                    >
                                                        Approve
                                                    </Button>
                                                    ) : (
                                                    "-"
                                                    )}
                                                </td>

                                                <td>{formatDate(row.soldDate)}</td>
                                                <td>{row.manHours ?? "-"}</td>
                                                <td>{row.scheduledHours ?? "-"}</td>
                                                <td>{row.loggedHours ?? "-"}</td>
                                                <td>{row.rep || "-"}</td>
                                                <td>{row.jobClass || "-"}</td>
                                                <td>{row.address || "-"}</td>

                                                <td style={{ maxWidth: 180 }}>
                                                    {Array.isArray(row.equipmentNames) &&
                                                    row.equipmentNames.length > 0 ? (
                                                    row.equipmentNames.map((name, i) => (
                                                        <div key={i}>{name}</div>
                                                    ))
                                                    ) : row.equipmentIds?.length ? (
                                                    row.equipmentIds.map((id) => {
                                                        const eq = equipment.find((e) => e.id === id);
                                                        return (
                                                        <div key={id}>
                                                            {eq?.equipmentName || "-"}
                                                        </div>
                                                        );
                                                    })
                                                    ) : (
                                                    "-"
                                                    )}
                                                </td>

                                                <td style={truncateStyle}>
                                                    {row.materialRequirements || "-"}
                                                </td>
                                                <td style={truncateStyle}>
                                                    {row.schedulingNotes || "-"}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    {jobs.length === 0 && (
                                    <tr>
                                        <td colSpan={11} style={{ textAlign: "center" }}>
                                        No job listings found.
                                        </td>
                                    </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>

                    {/* Pagination */}
                    {totalPages > 1 && (
                    <Row className="row-20">
                        <Col className="d-flex justify-content-center">
                            <Pagination aria-label="Job listings pagination" className="mb-0">
                                <PaginationItem disabled={page <= 1}>
                                    <PaginationLink first onClick={() => setPage(1)} />
                                </PaginationItem>
                                <PaginationItem disabled={page <= 1}>
                                    <PaginationLink
                                        previous
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    />
                                </PaginationItem>

                                {pageWindow.map((p) => (
                                    <PaginationItem key={p} active={p === page}>
                                        <PaginationLink onClick={() => setPage(p)}>
                                            {p}
                                        </PaginationLink>
                                    </PaginationItem>
                                ))}

                                <PaginationItem disabled={page >= totalPages}>
                                    <PaginationLink
                                        next
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    />
                                </PaginationItem>
                                <PaginationItem disabled={page >= totalPages}>
                                    <PaginationLink last onClick={() => setPage(totalPages)} />
                                </PaginationItem>
                            </Pagination>
                        </Col>
                    </Row>
                    )}
                </>
                )}

                {/* Filters Drawer */}
                <Offcanvas
                isOpen={filtersOpen}
                toggle={() => setFiltersOpen(false)}
                direction="end"
                >
                <OffcanvasHeader toggle={() => setFiltersOpen(false)}>
                    Filters
                </OffcanvasHeader>

                <OffcanvasBody>
                    {/* Job Class */}
                    <div style={{ marginBottom: 18 }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Job Class</div>

                    <FormGroup check>
                        <Label check>
                        <Input
                            type="checkbox"
                            checked={filters.jobClasses.includes("Const")}
                            onChange={(e) => {
                            const checked = e.target.checked;
                            setFilters((prev) => {
                                const set = new Set(prev.jobClasses);
                                checked
                                ? set.add("Const")
                                : set.delete("Const");
                                return { ...prev, jobClasses: Array.from(set) };
                            });
                            }}
                        />{" "}
                        Construction
                        </Label>
                    </FormGroup>

                    <FormGroup check className="mt-2">
                        <Label check>
                        <Input
                            type="checkbox"
                            checked={filters.jobClasses.includes("Maint")}
                            onChange={(e) => {
                            const checked = e.target.checked;
                            setFilters((prev) => {
                                const set = new Set(prev.jobClasses);
                                checked
                                ? set.add("Maint")
                                : set.delete("Maint");
                                return { ...prev, jobClasses: Array.from(set) };
                            });
                            }}
                        />{" "}
                        Maintenance
                        </Label>
                    </FormGroup>
                    </div>

                    <hr />

                    {/* Needs Update */}
                    <div style={{ marginBottom: 18 }}>
                    <div
                        style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        }}
                    >
                        <div>
                        <div style={{ fontWeight: 700 }}>Needs update</div>
                        <div
                            className="text-muted"
                            style={{ fontSize: 12, marginTop: 4 }}
                        >
                            Show jobs missing one or more fields.
                        </div>
                        </div>

                        <div
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                            setToggleMissingFilter((prev) => !prev);
                        }}
                        >
                        <BsChevronDown
                            style={{
                            transform: toggleMissingFilter ? "rotate(180deg)" : "",
                            }}
                        />
                        </div>
                    </div>

                    {toggleMissingFilter && (
                        <div style={{ marginTop: 14 }}>
                        <FormGroup check>
                            <Label check>
                            <Input
                                type="checkbox"
                                checked={!!filters.missing.manHours}
                                onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    missing: {
                                    ...prev.missing,
                                    manHours: e.target.checked,
                                    },
                                }))
                                }
                            />{" "}
                            Man Hours missing / 0
                            </Label>
                        </FormGroup>

                        <FormGroup check className="mt-2">
                            <Label check>
                            <Input
                                type="checkbox"
                                checked={!!filters.missing.equipment}
                                onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    missing: {
                                    ...prev.missing,
                                    equipment: e.target.checked,
                                    },
                                }))
                                }
                            />{" "}
                            No equipment assigned
                            </Label>
                        </FormGroup>

                        <FormGroup check className="mt-2">
                            <Label check>
                            <Input
                                type="checkbox"
                                checked={!!filters.missing.materialRequirements}
                                onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    missing: {
                                    ...prev.missing,
                                    materialRequirements: e.target.checked,
                                    },
                                }))
                                }
                            />{" "}
                            Material requirements empty
                            </Label>
                        </FormGroup>

                        <FormGroup check className="mt-2">
                            <Label check>
                            <Input
                                type="checkbox"
                                checked={!!filters.missing.schedulingNotes}
                                onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    missing: {
                                    ...prev.missing,
                                    schedulingNotes: e.target.checked,
                                    },
                                }))
                                }
                            />{" "}
                            Scheduling notes empty
                            </Label>
                        </FormGroup>

                        <FormGroup check className="mt-2">
                            <Label check>
                            <Input
                                type="checkbox"
                                checked={!!filters.missing.approved}
                                onChange={(e) =>
                                setFilters((prev) => ({
                                    ...prev,
                                    missing: {
                                    ...prev.missing,
                                    approved: e.target.checked,
                                    },
                                }))
                                }
                            />{" "}
                            Missing Approval
                            </Label>
                        </FormGroup>
                        </div>
                    )}
                    </div>

                    <hr />

                    {/* Hidden Job */}
                    <div style={{ marginBottom: 18 }}>
                    <div style={{ fontWeight: 700, marginBottom: 10 }}>Hidden Job</div>
                    <FormGroup check className="mt-2">
                        <Label check>
                        <Input
                            type="checkbox"
                            checked={!!filters.showHiddenJobs}
                            onChange={(e) =>
                            setFilters((prev) => ({
                                ...prev,
                                showHiddenJobs: e.target.checked,
                            }))
                            }
                        />{" "}
                        Show Hidden Jobs
                        </Label>
                    </FormGroup>
                    </div>

                    {/* Actions */}
                    <div style={{ display: "flex", gap: 10 }}>
                    <Button
                        color="secondary"
                        outline
                        onClick={() =>
                        setFilters({
                            jobClasses: [],
                            missing: {
                                manHours: false,
                                equipment: false,
                                materialRequirements: false,
                                schedulingNotes: false,
                                approved: false,
                            },
                            showHiddenJobs: false,
                        })
                        }
                        style={{ flex: 1 }}
                    >
                        Clear
                    </Button>

                    <Button
                        color="primary"
                        onClick={() => setFiltersOpen(false)}
                        style={{ flex: 1 }}
                    >
                        Done
                    </Button>
                    </div>
                </OffcanvasBody>
                </Offcanvas>

                {/* Merged Add / Edit Modal */}
                <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
                    <ModalHeader toggle={toggleModal}>
                        {modalMode === "add" ? "Add Job Listing" : "Edit Job Listing"}
                    </ModalHeader>

                    <ModalBody>
                        <Form>
                            {/* ── Header: compact read-only title if QB-linked, full fields otherwise ── */}
                            {hasInvoiceNumber ? (
                                /*
                                * This job is already linked to QuickBooks. Show the read-only
                                * Customer - Job Name header; identity fields are owned by QB.
                                */
                                <Row className="align-items-center" style={{ marginBottom: 12 }}>
                                    <Col md={8}>
                                        <div className="text-muted" style={{ fontSize: 14 }}>
                                        <strong>{jobTitle(form)}</strong>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup check style={{ marginTop: 6 }}>
                                        <Label check>
                                            <Input
                                            type="checkbox"
                                            checked={!!form.isOpen}
                                            onChange={(e) =>
                                                handleChange("isOpen", e.target.checked)
                                            }
                                            />{" "}
                                            Show Job Listing
                                        </Label>
                                        </FormGroup>
                                    </Col>
                                </Row>
                            ) : (
                                /*
                                * No QB invoice number - show all identity fields.
                                * Applies to both add mode AND edit mode for jobs not yet
                                * linked to QuickBooks. Invoice Number field is add-only.
                                */
                                <>
                                    <Row className="align-items-center">
                                        <Col md={5}>
                                            <FormGroup>
                                                <Label for="selectedQuickBooksCustomerListId">QuickBooks Customer</Label>
                                                <AsyncSelect
                                                    cacheOptions
                                                    defaultOptions={false}
                                                    loadOptions={loadQuickBooksCustomerOptions}
                                                    value={selectedQuickBooksCustomerOption}
                                                    onChange={(selected) => {
                                                        handleChange("selectedQuickBooksCustomerListId", selected?.value ?? null);
                                                        handleChange("customerName", selected?.label ?? "");
                                                    }}
                                                    placeholder="Search QuickBooks customer..."
                                                    inputId="selectedQuickBooksCustomerListId"
                                                    noOptionsMessage={({ inputValue }) =>
                                                        (inputValue || "").trim().length < QB_CUSTOMER_MIN_SEARCH
                                                            ? "Type 3+ characters to search"
                                                            : "No customers found"
                                                    }
                                                    isClearable
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={4}>
                                            <FormGroup>
                                                <Label for="soldDate">Sold Date</Label>
                                                <Input
                                                type="date"
                                                id="soldDate"
                                                value={form.soldDate?.slice(0, 10) || ""}
                                                onChange={(e) =>
                                                    handleChange("soldDate", e.target.value)
                                                }
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={3}>
                                            <FormGroup check style={{ marginTop: 6 }}>
                                            <Label check>
                                                <Input
                                                type="checkbox"
                                                checked={!!form.isOpen}
                                                onChange={(e) =>
                                                    handleChange("isOpen", e.target.checked)
                                                }
                                                />{" "}
                                                Show Job Listing
                                            </Label>
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label for="jobName">Job Name</Label>
                                                <Input
                                                type="text"
                                                id="jobName"
                                                placeholder="Job name - job name"
                                                value={form.jobName || ""}
                                                onChange={(e) =>
                                                    handleChange("jobName", e.target.value)
                                                }
                                                />
                                            </FormGroup>
                                        </Col>

                                        {/* <Col md={4}>
                                            <FormGroup>
                                            <Label for="invoiceNumber">Invoice Number</Label>
                                            <Input
                                                type="text"
                                                id="invoiceNumber"
                                                placeholder="Optional: QuickBooks Job List ID"
                                                value={form.invoiceNumber || ""}
                                                onChange={(e) =>
                                                handleChange("invoiceNumber", e.target.value)
                                                }
                                            />
                                            </FormGroup>
                                        </Col> */}
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="rep">Rep</Label>
                                                <Select
                                                options={repOptions}
                                                value={
                                                    repOptions.find((o) => o.value === form.rep) ?? null
                                                }
                                                onChange={(selected) =>
                                                    handleChange("rep", selected?.value ?? "")
                                                }
                                                placeholder="Select rep..."
                                                inputId="rep"
                                                />
                                            </FormGroup>
                                        </Col>

                                        <Col md={6}>
                                            <FormGroup>
                                                <Label for="jobClass">Job Class</Label>
                                                <Select
                                                options={jobOptions}
                                                value={
                                                    jobOptions.find(
                                                    (o) => o.value === form.jobClass
                                                    ) ?? null
                                                }
                                                onChange={(selected) =>
                                                    handleChange("jobClass", selected?.value ?? "")
                                                }
                                                placeholder="Select job class..."
                                                inputId="jobClass"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            {/* ── Shared fields (always shown) ── */}
                            <FormGroup>
                                <Label for="materialRequirements">Material Requirements</Label>
                                <Input
                                id="materialRequirements"
                                type="textarea"
                                rows={4}
                                value={form.materialRequirements || ""}
                                onChange={(e) =>
                                    handleChange("materialRequirements", e.target.value)
                                }
                                placeholder="e.g. Two tons weather boulders..."
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="schedulingNotes">Customer Scheduling Notes</Label>
                                <Input
                                id="schedulingNotes"
                                type="textarea"
                                rows={4}
                                value={form.schedulingNotes || ""}
                                onChange={(e) =>
                                    handleChange("schedulingNotes", e.target.value)
                                }
                                placeholder="e.g. Customer requests work after 2pm..."
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="address">Address</Label>
                                <Input
                                id="address"
                                value={form.address || ""}
                                onChange={(e) => handleChange("address", e.target.value)}
                                placeholder="Enter the address of the job site..."
                                />
                            </FormGroup>

                            <Row>
                                <Col md={8}>
                                    <FormGroup>
                                        <Label for="equipmentId">Equipment</Label>
                                        <Select
                                        isMulti
                                        options={equipmentOptions}
                                        value={selectedEquipmentOptions}
                                        onChange={(selected) => {
                                            const ids = (selected || []).map((s) => s.value);
                                            handleChange("equipmentIds", ids);
                                        }}
                                        placeholder="Select equipment..."
                                        closeMenuOnSelect={false}
                                        />
                                    </FormGroup>
                                </Col>

                                <Col md={4}>
                                    <FormGroup>
                                        <Label for="manHours">Man Hours</Label>
                                        <Input
                                        id="manHours"
                                        type="number"
                                        min="0"
                                        value={form.manHours ?? ""}
                                        onChange={(e) =>
                                            handleChange("manHours", e.target.value)
                                        }
                                        placeholder="0"
                                        />
                                    </FormGroup>

                                    <div style={{ marginTop: -6, fontWeight: 600 }}>
                                        {crewDaysLabel}
                                    </div>
                                    <div className="text-muted" style={{ fontSize: 12 }}>
                                        (1 crew day = 20 hours)
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </ModalBody>

                    <ModalFooter className="d-flex justify-content-between">
                        {modalMode === "edit" && !hasInvoiceNumber ? (
                            <Button 
                            color="danger" 
                            outline 
                            onClick={() => removeJob(form.id)}
                            >
                                Remove Job Listing
                            </Button>
                        ) : (
                            /* Spacer so Save/Cancel stay right-aligned in add mode */
                            <div />
                        )}

                        <div className="d-flex gap-2">
                            <Button
                                color="secondary"
                                onClick={toggleModal}
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button color="primary" onClick={handleSave} disabled={saving}>
                                {saving ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default JobListings;